import { Controller } from "@hotwired/stimulus"

// Progressive enhancement for the public landing page.
//
// 1. Scroll reveals: fades sections up as they enter the viewport. The initial
//    hidden state is scoped to `.is-enhanced` (added here on connect), so a
//    no-JS or reduced-motion render shows every section fully visible.
// 2. Funnel sync: as each pipeline stage crosses the vertical centre, its
//    matching band in the sticky funnel diagram lights up.
export default class extends Controller {
  static targets = ["reveal", "stage", "band"]

  connect() {
    this.#reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    if (!this.#reducedMotion && "IntersectionObserver" in window) {
      this.element.classList.add("is-enhanced")
      this.#observeReveals()
    }

    this.#observeStages()
    this.#activate(0)
  }

  disconnect() {
    this.#revealObserver?.disconnect()
    this.#stageObserver?.disconnect()
  }

  #observeReveals() {
    this.#revealObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          entry.target.classList.add("is-visible")
          this.#revealObserver.unobserve(entry.target)
        }
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.15 }
    )

    this.revealTargets.forEach((el) => this.#revealObserver.observe(el))
  }

  #observeStages() {
    if (!("IntersectionObserver" in window) || this.stageTargets.length === 0) return

    // A thin band across the vertical centre: whichever stage sits in it wins.
    this.#stageObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          this.#activate(Number(entry.target.dataset.stageIndex))
        }
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    )

    this.stageTargets.forEach((el) => this.#stageObserver.observe(el))
  }

  #activate(index) {
    if (index === this.#active) return
    this.#active = index

    this.bandTargets.forEach((band) => {
      band.classList.toggle("is-active", Number(band.dataset.stageIndex) === index)
    })
    this.stageTargets.forEach((stage) => {
      stage.classList.toggle("is-current", Number(stage.dataset.stageIndex) === index)
    })
  }

  #reducedMotion = false
  #active = -1
  #revealObserver = null
  #stageObserver = null
}
